import { useContext } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { SplitDetailPanelStoreContext } from '../context';
import { shallowEqual } from '../lib/equality';

import type { SplitDetailPanelActions, SplitDetailPanelStore, TabItem, TabKey } from '../types';

/**
 * Hook to access the DetailPanel store instance from context.
 * Throws an error if used outside of SplitDetailPanelProvider.
 *
 * @example
 * ```tsx
 * const { store, maxTabs } = useSplitDetailPanelContext();
 * const state = store.getState();
 * ```
 */
export function useSplitDetailPanelContext() {
  const context = useContext(SplitDetailPanelStoreContext);

  if (!context) {
    throw new Error('useSplitDetailPanelContext must be used within a SplitDetailPanelProvider');
  }

  return context;
}

/**
 * Hook to subscribe to specific parts of the DetailPanel store state.
 * Uses shallow equality by default to prevent unnecessary re-renders.
 *
 * @param selector - Function to select specific state
 * @param equalityFn - Optional custom equality function (defaults to shallow)
 *
 * @example
 * ```tsx
 * // Subscribe to tab count only
 * const tabCount = useSplitDetailPanelSelector(state => state.tabs.length);
 *
 * // Subscribe to active tab key
 * const activeTabKey = useSplitDetailPanelSelector(state => state.activeTabKey);
 * ```
 */
export function useSplitDetailPanelSelector<T>(
  selector: (state: SplitDetailPanelStore) => T,
  equalityFn: (a: T, b: T) => boolean = shallowEqual as (a: T, b: T) => boolean,
): T {
  const { store } = useSplitDetailPanelContext();
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

/**
 * Hook to get only the actions from the DetailPanel store.
 * Actions are stable references and won't cause re-renders.
 *
 * @note Phase 6에서 useSplitDetailPanelActions에서 리네이밍됨
 *
 * @example
 * ```tsx
 * const actions = useTabPanelActionStoreInternal();
 *
 * const handleOpenTab = (item: ResourceItem) => {
 *   actions.openTab('subnet', item, SubnetPanel);
 * };
 *
 * const handleCloseAll = () => {
 *   actions.closeAllTabs(); // Close all tabs
 *   // or
 *   actions.closeAllTabs('record'); // Close only 'record' type tabs
 * };
 * ```
 */
export function useTabPanelActionStoreInternal(): SplitDetailPanelActions {
  const { store } = useSplitDetailPanelContext();

  return useStoreWithEqualityFn(
    store,
    state => ({
      closeAllTabs: state.closeAllTabs,
      closeTab: state.closeTab,
      openTab: state.openTab,
      reconcileTabs: state.reconcileTabs,
      reset: state.reset,
      setVisible: state.setVisible,
      switchTab: state.switchTab,
    }),
    () => true, // Actions are stable, never re-render
  );
}

/**
 * @deprecated Use useTabPanelActionStoreInternal instead
 */
export const useSplitDetailPanelActions = useTabPanelActionStoreInternal;

/**
 * Hook to get the tabs array from the DetailPanel store.
 *
 * @example
 * ```tsx
 * const tabs = useSplitDetailPanelTabs();
 * ```
 */
export function useSplitDetailPanelTabs(): TabItem[] {
  return useSplitDetailPanelSelector(state => state.tabs, Object.is);
}

/**
 * Hook to get the active tab key from the DetailPanel store.
 *
 * @example
 * ```tsx
 * const activeTabKey = useSplitDetailPanelActiveTabKey();
 * ```
 */
export function useSplitDetailPanelActiveTabKey(): TabKey | null {
  return useSplitDetailPanelSelector(state => state.activeTabKey, Object.is);
}

/**
 * Hook to get the currently active tab from the DetailPanel store.
 * Returns undefined if no tab is active.
 *
 * @example
 * ```tsx
 * const activeTab = useSplitDetailPanelActiveTab();
 *
 * if (activeTab) {
 *   const { component: TabComponent, item } = activeTab;
 *   return <TabComponent item={item} />;
 * }
 * ```
 */
export function useSplitDetailPanelActiveTab(): TabItem | undefined {
  return useSplitDetailPanelSelector(state => {
    if (!state.activeTabKey) return undefined;
    return state.tabs.find(tab => tab.key === state.activeTabKey);
  }, Object.is);
}

/**
 * Hook to get the visibility state from the DetailPanel store.
 *
 * @example
 * ```tsx
 * const visible = useSplitDetailPanelVisible();
 * ```
 */
export function useSplitDetailPanelVisible(): boolean {
  return useSplitDetailPanelSelector(state => state.visible, Object.is);
}

/**
 * Hook to get the tab count from the DetailPanel store.
 *
 * @example
 * ```tsx
 * const tabCount = useSplitDetailPanelTabCount();
 * ```
 */
export function useSplitDetailPanelTabCount(): number {
  return useSplitDetailPanelSelector(state => state.tabs.length, Object.is);
}

/**
 * Hook to get the max tabs limit from the context.
 *
 * @example
 * ```tsx
 * const maxTabs = useSplitDetailPanelMaxTabs();
 * const canAddMoreTabs = tabCount < maxTabs;
 * ```
 */
export function useSplitDetailPanelMaxTabs(): number {
  const { maxTabs } = useSplitDetailPanelContext();
  return maxTabs;
}

/**
 * Hook to check if more tabs can be added.
 *
 * @example
 * ```tsx
 * const canAddMore = useSplitDetailPanelCanAddTab();
 *
 * if (!canAddMore) {
 *   toast.warn('Maximum tabs limit reached');
 * }
 * ```
 */
export function useSplitDetailPanelCanAddTab(): boolean {
  const { store, maxTabs } = useSplitDetailPanelContext();
  return useStoreWithEqualityFn(store, state => state.tabs.length < maxTabs, Object.is);
}

/**
 * Hook to get derived state from the DetailPanel store.
 * Combines multiple related values into a single subscription.
 *
 * @example
 * ```tsx
 * const { tabCount, activeTab, visible, canAddMore } = useSplitDetailPanelDerivedState();
 * ```
 */
export function useSplitDetailPanelDerivedState() {
  const { store, maxTabs } = useSplitDetailPanelContext();

  return useStoreWithEqualityFn(
    store,
    state => {
      const activeTab = state.activeTabKey
        ? state.tabs.find(tab => tab.key === state.activeTabKey)
        : undefined;

      return {
        activeTab,
        canAddMore: state.tabs.length < maxTabs,
        tabCount: state.tabs.length,
        visible: state.visible,
      };
    },
    (
      a: {
        activeTab: TabItem | undefined;
        canAddMore: boolean;
        tabCount: number;
        visible: boolean;
      },
      b: {
        activeTab: TabItem | undefined;
        canAddMore: boolean;
        tabCount: number;
        visible: boolean;
      },
    ) =>
      a.activeTab === b.activeTab &&
      a.canAddMore === b.canAddMore &&
      a.tabCount === b.tabCount &&
      a.visible === b.visible,
  );
}

/**
 * Hook to access panel preferences from bridge (enable, type, size, available).
 * These values come from the legacy panel system via adapter.
 *
 * @example
 * ```tsx
 * const { enablePanel, panelType, setPanelType } = useSplitDetailPanelPreferences();
 * ```
 */
export function useSplitDetailPanelPreferences() {
  const context = useSplitDetailPanelContext();

  return {
    enablePanel: context.enablePanel,
    availablePanel: context.availablePanel,
    panelType: context.panelType,
    panelSize: context.panelSize,
    setPanelType: context.setPanelType,
    setPanelSize: context.setPanelSize,
    setEnablePanel: context.setEnablePanel,
  };
}
